import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config';
import { handleFirebaseError } from '../../error-handling';
import type { Organization } from '@/types/organization';

export async function getUserOrganizations(userId: string, role?: string): Promise<Organization[]> {
  try {
    // Get the user's organization memberships
    let membershipQuery;
    if (role) {
      membershipQuery = query(
        collection(db, 'organizationMembers'),
        where('userId', '==', userId),
        where('role', '==', role)
      );
    } else {
      membershipQuery = query(
        collection(db, 'organizationMembers'),
        where('userId', '==', userId)
      );
    }
    
    const membershipSnapshot = await getDocs(membershipQuery);
    if (membershipSnapshot.empty) {
      return [];
    }

    // Get organization details for each membership
    const organizations = await Promise.all(
      membershipSnapshot.docs.map(async (memberDoc) => {
        const membership = memberDoc.data();
        const orgDoc = await getDoc(doc(db, 'organizations', membership.organizationId));
        
        if (!orgDoc.exists()) {
          return null;
        }

        return {
          id: orgDoc.id,
          ...orgDoc.data(),
          createdAt: orgDoc.data().createdAt?.toDate(),
          updatedAt: orgDoc.data().updatedAt?.toDate()
        } as Organization;
      })
    );

    return organizations.filter((org): org is Organization => org !== null);
  } catch (error) {
    handleFirebaseError(error, 'Error fetching user organizations');
    return [];
  }
}

export async function getUserCustomerOnlyOrganizations(userId: string): Promise<Organization[]> {
  try {
    // Get all organization memberships for this user
    const membershipQuery = query(
      collection(db, 'organizationMembers'),
      where('userId', '==', userId)
    );
    
    const membershipSnapshot = await getDocs(membershipQuery);
    if (membershipSnapshot.empty) {
      return [];
    }

    // Group memberships by organizationId
    const orgMemberships = new Map<string, string[]>();
    membershipSnapshot.docs.forEach(doc => {
      const membership = doc.data();
      const orgId = membership.organizationId;
      const role = membership.role;
      
      if (!orgMemberships.has(orgId)) {
        orgMemberships.set(orgId, []);
      }
      orgMemberships.get(orgId)!.push(role);
    });

    // Find organizations where user has ONLY 'customer' role
    const customerOnlyOrgIds = Array.from(orgMemberships.entries())
      .filter(([orgId, roles]) => roles.length === 1 && roles[0] === 'customer')
      .map(([orgId]) => orgId);

    if (customerOnlyOrgIds.length === 0) {
      return [];
    }

    // Get organization details for customer-only organizations
    const organizations = await Promise.all(
      customerOnlyOrgIds.map(async (orgId) => {
        const orgDoc = await getDoc(doc(db, 'organizations', orgId));
        
        if (!orgDoc.exists()) {
          return null;
        }

        return {
          id: orgDoc.id,
          ...orgDoc.data(),
          createdAt: orgDoc.data().createdAt?.toDate(),
          updatedAt: orgDoc.data().updatedAt?.toDate()
        } as Organization;
      })
    );

    return organizations.filter((org): org is Organization => org !== null);
  } catch (error) {
    handleFirebaseError(error, 'Error fetching user customer-only organizations');
    return [];
  }
}

export async function getExaminerOrganizations(userId: string): Promise<Organization[]> {
  return getUserOrganizations(userId, 'examiner');
} 